/**
 * applySettingToSelection.ts - Run a {@link TableSetting} (for example "Update lookup field values")
 * against the selected cells and persist the cells it produces.
 */

import assert from 'assert';

import type {Cell} from 'notebook/tables/constants/CellConstants';
import type {TableSetting} from 'notebook/tables/constants/TableConstants';
import type {TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {persistTable} from 'notebook/tables/store/logic/mutations/mutationParams';

/**
 * Replace every cell of the table with `newCells` and re-evaluate the whole table through an
 * `eval-table` op. The rest of the table (name, widths, etc.) is read at call time.
 */
export function replaceCells(params: TableMutationParams, newCells: Array<Array<Cell>>): void {
  const {table} = params.getTableProps();
  persistTable(params, {
    table: {...table, table: newCells},
    tableOp: {type: 'eval-table'},
  });
}

/**
 * Call `applySetting` with the merge-expanded selection box and the current table, binding its
 * `onUpdateCells` callback to {@link replaceCells}.
 *
 * Settings may call `onUpdateCells` asynchronously (the lookup-field setting waits on a server
 * request). The cells they pass are derived from the table given here, so any cell edits made in
 * the meantime are overwritten.
 */
export function applySettingToSelection(
  params: TableMutationParams,
  applySetting: TableSetting['applySettingToSelection']
): void {
  const {selection} = params.getTableModel();
  assert(selection.hasRealSelection, 'Expected to have a real selection');
  assert(selection.expandedBox != null, 'Expected to have a selection');
  const {minI, maxI, minJ, maxJ} = selection.expandedBox;
  applySetting({
    minI,
    maxI,
    minJ,
    maxJ,
    table: params.getTableProps().table,
    onUpdateCells: (newCells) => {
      replaceCells(params, newCells);
    },
  });
}
